/**
 * Session identity for the event stream.
 *
 * A session is what the member experienced: one stretch of using the app. It is
 * emphatically not a sign-in — the access token lives fourteen days, so somebody who
 * opens the app every day may sign in once a month. Counting visits by sign-in would
 * understate engagement by an order of magnitude, which is why this exists at all.
 *
 * The id is mirrored to storage so that a cold start within the idle window continues
 * the same session. That is the normal case on a phone, where the OS kills
 * backgrounded apps freely, and treating each relaunch as a new visit would inflate
 * the count just as badly as sign-ins deflate it.
 */

import { getItem, removeItem, setItem, StorageKey } from "~/lib/storage";

/** Matches `analytics_session_idle_minutes` on the API. */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

interface SessionRecord {
  id: string;
  user_id: string;
  started_at: string;
  last_seen_at: number;
}

let current: SessionRecord | null = null;

function mint(userId: string, now: number): SessionRecord {
  return {
    id: crypto.randomUUID(),
    user_id: userId,
    started_at: new Date(now).toISOString(),
    last_seen_at: now,
  };
}

/**
 * Load any session left behind by a previous run of the app.
 *
 * A record belonging to a different account is discarded rather than adopted: on a
 * shared device the previous member's visit is not this member's.
 */
export async function restoreSession(userId: string): Promise<void> {
  const raw = await getItem(StorageKey.AnalyticsSession);
  if (!raw) return;
  try {
    const stored = JSON.parse(raw) as SessionRecord;
    if (stored.user_id !== userId) {
      await removeItem(StorageKey.AnalyticsSession);
      return;
    }
    current = stored;
  } catch {
    // A truncated write from a kill mid-persist. Start a fresh session.
    await removeItem(StorageKey.AnalyticsSession);
  }
}

/**
 * The id to stamp on an event now, minting a new session if the old one has expired.
 *
 * Returns `{ id, isNew }` so the caller can emit `session_start` exactly once per
 * session without this module needing to know that the event exists.
 */
export function currentSession(
  userId: string,
  now: number = Date.now(),
): { id: string; isNew: boolean } {
  const expired =
    current !== null && now - current.last_seen_at > IDLE_TIMEOUT_MS;
  // An account change always starts a new session, however little time has passed:
  // two members' activity must never share one visit.
  const foreign = current !== null && current.user_id !== userId;

  if (current === null || expired || foreign) {
    current = mint(userId, now);
    void persist();
    return { id: current.id, isNew: true };
  }

  current.last_seen_at = now;
  return { id: current.id, isNew: false };
}

/** Write the session through, so a cold start inside the window can continue it. */
export async function persist(): Promise<void> {
  if (current === null) return;
  await setItem(StorageKey.AnalyticsSession, JSON.stringify(current));
}

/** Forget the session. Called on sign-out and account deletion. */
export async function discardSession(): Promise<void> {
  current = null;
  await removeItem(StorageKey.AnalyticsSession);
}

/** Reset module state without touching storage. Tests only. */
export function resetSessionForTests(): void {
  current = null;
}
